import sortBy from 'lodash/sortBy';

import { LessonMaterialFragment } from 'approot/shared/lesson-materials/__generated__/LessonMaterialFragment';
import {
  LEARNING_SEQUENCE_LESSON_MATERIAL_GROUP_TYPE_CLASS,
  LEARNING_SEQUENCE_LESSON_MATERIAL_GROUP_TYPE_GROUP,
  LEARNING_SEQUENCE_LESSON_MATERIAL_GROUP_TYPE_STUDENT,
} from 'global/constants';
import { getDefaultUnitOfMeasurement } from 'approot/activity-builder/components/teaching-notes/lesson-materials/units-of-measurement/units-of-measurement-utils';
import {
  LessonMaterialCalculatorMap,
  LessonMaterialCalculatorMapItem,
  LessonMaterialCalculatorNote,
  MaterialQuantityListItem,
} from './lesson-materials-calculator.constants';

export const calculateMaterialQuantityPerClass = ({
  numberOfItems,
  groupType,
  numberInGroup,
  classSize,
}: {
  numberOfItems: number;
  groupType: string;
  numberInGroup: number;
  classSize: number;
}): number => {
  let result: number;
  switch (groupType) {
    case LEARNING_SEQUENCE_LESSON_MATERIAL_GROUP_TYPE_GROUP:
      result = (classSize / Math.max(1, numberInGroup)) * numberOfItems;
      break;
    case LEARNING_SEQUENCE_LESSON_MATERIAL_GROUP_TYPE_CLASS:
      result = numberOfItems;
      break;
    case LEARNING_SEQUENCE_LESSON_MATERIAL_GROUP_TYPE_STUDENT:
    default:
      result = classSize * numberOfItems;
      break;
  }
  return Math.ceil(result);
};

export function calculateTotalNormalizedQuantity({
  quantityList,
  useNormalisedQuantity,
  classSize,
  isReusable,
}: {
  quantityList: MaterialQuantityListItem[];
  useNormalisedQuantity: boolean;
  classSize: number;
  isReusable: boolean;
}): number {
  const normalisedQuantity = quantityList.reduce((acc, quantity) => {
    const quantityValue = useNormalisedQuantity
      ? quantity.normalisedQuantity || 1
      : quantity.quantity;

    const calculatedQuantity = calculateMaterialQuantityPerClass({
      numberOfItems: quantityValue,
      groupType: quantity.groupType || '',
      numberInGroup: quantity.numberInGroup || 1,
      classSize,
    });

    return isReusable
      ? Math.max(acc, calculatedQuantity)
      : acc + calculatedQuantity;
  }, 0);
  return normalisedQuantity;
}

export function combineLessonMaterialQuantities({
  currentMaterialMapItem,
  lessonMaterial,
  defaultUnitOfMeasurementId,
}: {
  currentMaterialMapItem: LessonMaterialCalculatorMapItem;
  lessonMaterial: LessonMaterialFragment;
  defaultUnitOfMeasurementId: number | null;
}) {
  currentMaterialMapItem.quantityList.push({
    quantity: lessonMaterial.numberOfItems,
    unitOfMeasurement: lessonMaterial.unitOfMeasurementId || 0,
    normalisedQuantity: lessonMaterial.normalisedNumberOfItems,
    numberInGroup: lessonMaterial.numberInGroup,
    groupType: lessonMaterial.groupType,
    note: lessonMaterial.note,
  });
  // add new unit of measurement to the set
  if (lessonMaterial.unitOfMeasurementId && defaultUnitOfMeasurementId) {
    currentMaterialMapItem.unitsOfMeasurementsUsedSet.add(
      lessonMaterial.unitOfMeasurementId
    );
    // if there are multiple units of measurement use the normalised quantities
    if (currentMaterialMapItem.unitsOfMeasurementsUsedSet.size > 1) {
      currentMaterialMapItem.useNormalisedQuantity = true;
      currentMaterialMapItem.unitOfMeasurementId = defaultUnitOfMeasurementId;
    }
  }

  return currentMaterialMapItem;
}

/**
 * NOTE:
 * All the materials calculator logic is duplicated in the backend for document downloads.
 * Any changes here need to be reflected there.
 */

export const calculateRequiredLessonMaterials = ({
  notes,
  classSize,
}: {
  notes: LessonMaterialCalculatorNote[];
  classSize: number;
}): LessonMaterialFragment[] => {
  const filteredMaterials = notes
    .flatMap(n => n.lessonMaterials)
    .filter(Boolean) as LessonMaterialFragment[];

  const materialsMap = filteredMaterials.reduce((acc, lessonMaterial) => {
    const defaultUnitOfMeasurementId =
      getDefaultUnitOfMeasurement(lessonMaterial)?.id || null;

    // we are not showing legacy materials in the calculator
    // we need to remove legacy materials from the database
    if (!lessonMaterial.materialId) {
      return acc;
    }

    let currentMaterialMapItem = acc.get(lessonMaterial.materialId);

    if (!currentMaterialMapItem) {
      currentMaterialMapItem = {
        ...lessonMaterial,
        // add extra props to the object to help with calculations
        quantityList: [],
        unitsOfMeasurementsUsedSet: new Set(),
        useNormalisedQuantity: false,
        defaultUnitOfMeasurementId,
      };
      acc.set(lessonMaterial.materialId, currentMaterialMapItem);
    }
    currentMaterialMapItem = combineLessonMaterialQuantities({
      currentMaterialMapItem,
      lessonMaterial,
      defaultUnitOfMeasurementId,
    });

    return acc;
  }, new Map() as LessonMaterialCalculatorMap);

  for (const [key, material] of materialsMap) {
    const updatedNumberOfItems = calculateTotalNormalizedQuantity({
      quantityList: material.quantityList,
      useNormalisedQuantity: material.useNormalisedQuantity,
      classSize,
      isReusable: material.isReusable || false,
    });

    const updatedNotes =
      material.quantityList
        .map(q => q.note)
        .filter(Boolean)
        .join(' / ') || null;

    const updatedMaterial = {
      ...material,
      numberOfItems: updatedNumberOfItems,
      normalisedNumberOfItems: null, // we don't need this anymore
      note: updatedNotes,
    };

    materialsMap.set(key, updatedMaterial);
  }

  const sortedMaterials = sortBy(Array.from(materialsMap.values()), m =>
    m.name.toLowerCase()
  );

  // remove the extra props from the objects and return them as a list
  const calculatedMaterials = sortedMaterials.map(m => {
    const {
      quantityList,
      useNormalisedQuantity,
      unitsOfMeasurementsUsedSet: unitsOfMeasurementsUsed,
      defaultUnitOfMeasurementId,
      ...rest
    } = m;
    return rest;
  });

  return calculatedMaterials;
};
